import React from 'react';
import PropTypes from 'prop-types';

import Image from '$partials/media/image';
import RichText from '$partials/global/rich-text';
import ModuleModal from './modal';
import { getBgColor } from '$utilities/data';
import { HeaderCardItem } from '$partials/modules/individual/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const CardItem = ({
    backgroundColor,
    cardSize,
    title,
    eyebrowIcon,
    eyebrowText,
    media = {},
    media2 = {},
    modal,
    textContent,
}) => {

    const imageOnly = (!eyebrowIcon && !eyebrowText && !textContent && !title);

    return (
        cardSize === 'Large' ? (
            <div className='w-full px-10 sm:px-5 md:px-10'>
                <div
                    className='overflow-hidden rounded-button bg-sand'>

                    {!imageOnly && (
                        <div className='px-30 py-40 md:p-40 h-full flex flex-col gap-y-20 relative'>
                            {/* Eyebrow */}
                            {(eyebrowIcon || eyebrowText) && (
                                <div className='flex flex-row items-center'>
                                    {eyebrowIcon && (
                                        <div
                                            className='mr-10'
                                            style={{
                                                fontSize: '18px',
                                                width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em` ,
                                                maxWidth: '100%'
                                            }}>
                                            <Image
                                                {...eyebrowIcon}
                                                loading={''}/>
                                        </div>
                                    )}
                                    <p className='type-style-7 text-orange'>
                                        {eyebrowText}
                                    </p>
                                </div>
                            )}

                            {title && (
                                <p className='type-style-3'>
                                    {title}
                                </p>
                            )}
                            {textContent && (
                                <RichText
                                    className='type-style-5 rich-text'
                                    {...textContent}/>
                            )}

                            {modal && !media && (
                                <div>
                                    <ModuleModal
                                        buttonColour='bg-white'
                                        {...modal}/>
                                </div>
                            )}
                        </div>
                    )}
                    <div className='relative h-full'>
                        {media && (
                            media?.contentType?.includes('video/') ? (
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    src={media?.src}
                                    width={'100%'}/>
                            ) :  <Image
                                loadWidth={840}
                                {...media}
                                style={{width: undefined}} />
                        )}
                        {modal && media && (
                            <div className='absolute bottom-0 left-0 m-40'>
                                <ModuleModal
                                    buttonColour='bg-white'
                                    {...modal}/>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        ) : (
            <div className='w-full grid grid-cols-2'>
                <div className='px-10 sm:px-5 md:px-10'>

                    <div
                        className='overflow-hidden rounded-button aspect-square'
                        style={{ backgroundColor: `${getBgColor(backgroundColor)}` }}>
                        <div className='h-full w-full'>

                            {media && (
                                media?.contentType?.includes('video/') ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className='w-full h-full object-cover'
                                        src={media?.src}
                                        width={'100%'}/>
                                ) :  <Image
                                    className='w-full h-full object-cover'
                                    containerClassName='w-full h-full relative'
                                    loadWidth={840}
                                    {...media}
                                    width={'100%'} />
                            )}
                        </div>
                    </div>
                </div>
                <div className='px-10 sm:px-5 md:px-10'>
                    <div
                        className='overflow-hidden rounded-button aspect-square'
                        style={{ backgroundColor: `${getBgColor(backgroundColor)}` }}>
                        <div className='h-full w-full'>
                            {media2 && (
                                media2?.contentType?.includes('video/') ? (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className='w-full h-full object-cover'
                                        src={media2?.src}
                                        width={'100%'}/>
                                ) :  <Image
                                    className='w-full h-full object-cover'
                                    containerClassName='w-full h-full relative'
                                    loadWidth={840}
                                    {...media2}
                                    width={'100%'} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */

// Text Cards and Image Cards
const ModuleLandingPageCards = ({
    column1Collection,
    column2Collection,
    title,
    parent,
}) => {
    const column1items = column1Collection?.items;
    const column2items = column2Collection?.items;

    const combinedArray = [];

    // Reorder columns so cards stack in correct order on mobile
    for (let i = 0; i < Math.max(column1Collection?.items?.length, column2Collection?.items?.length); i++) {
        if (i < column1Collection?.items?.length) {
            combinedArray.push(column1Collection?.items?.[i]);
        }
        if (i < column2Collection?.items?.length) {
            combinedArray.push(column2Collection?.items?.[i]);
        }
    }
    return (
        <div className={`bg-white relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>

                <div className='bg-white'>
                    {title && (
                        <p className='px-10 sm:px-5 md:px-10 type-style-3 mb-20 md:mb-40 sm:w-8/12 md:w-7/12'>
                            {title}
                        </p>
                    )}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-20'>
                        <div className='hidden sm:flex w-full flex-col gap-y-20 sm:max-md:gap-y-10'>
                            {column1items?.map((item, i) =>
                                item ? (
                                    item?.__typename === 'BvaHeaderCardItem' ? (
                                        <HeaderCardItem
                                            index={0}
                                            key={i}
                                            parent='Landing'
                                            {...item} />
                                    ) : (
                                        <CardItem
                                            key={i}
                                            {...item} />
                                    )
                                ) : null
                            )}
                        </div>
                        <div className='hidden sm:flex w-full flex-col gap-y-20 sm:max-md:gap-y-10'>
                            {column2items?.map((item, i) =>
                                item ? (
                                    item?.__typename === 'BvaHeaderCardItem' ? (
                                        <HeaderCardItem
                                            index={1}
                                            key={i}
                                            parent='Landing'
                                            {...item} />
                                    ) : (
                                        <CardItem
                                            key={i}
                                            {...item} />
                                    )
                                ) : null
                            )}
                        </div>
                        <div className={`flex sm:hidden w-full flex-col
                        ${combinedArray?.[0]?.__typename === 'BvaHeaderCardItem' ? '' : 'gap-y-20'}`}>
                            {combinedArray?.map((item, i) =>
                                item ? (
                                    item?.__typename === 'BvaHeaderCardItem' ? (
                                        <HeaderCardItem
                                            index={1}
                                            key={i}
                                            parent='Landing'
                                            {...item} />
                                    ) : (
                                        <CardItem
                                            key={i}
                                            {...item} />
                                    )
                                ) : null
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

CardItem.propTypes = {
    backgroundColor: PropTypes.string,
    cardSize: PropTypes.string,
    modal: PropTypes.object,
    textContent: PropTypes.object,
    title: PropTypes.string,
    eyebrowText: PropTypes.string,
    eyebrowIcon: PropTypes.shape({
        src: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired
    }),
    media: PropTypes.shape({
        src: PropTypes.string.isRequired
    }),
    media2: PropTypes.shape({
        src: PropTypes.string.isRequired
    }),
};

ModuleLandingPageCards.propTypes = {
    column1Collection: PropTypes.object,
    column2Collection: PropTypes.object,
    title: PropTypes.string,
    parent: PropTypes.string
};

export default ModuleLandingPageCards;
